import { DashboardStat, Stat } from '@apps/admin/routes/DashboardPreview/components/DashboardChecklist/components/DashboardStats';
import { DashboardWebsitePreview } from '@apps/admin/routes/DashboardPreview/components/DashboardChecklist/components/DashboardWebsitePreview';
import { useGetWebsitePreviewDataQuery } from '@graphql/generated';
import { useRouterHelper, useTranslation } from '@shared/core';
import { animationTransition } from '@shared/utils/animationTransition';
import { ComponentWithAs } from '@shared/utils/forwardRef';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';
import { Box, Flex, FlexProps, TextV2 } from '@withjoy/joykit';
import { GuestList, CheckOutline, Gift } from '@withjoy/joykit/icons';
import { format } from 'date-fns';
import React from 'react';
import { routePaths } from '@apps/admin/route.paths';
import { useLoadGoogleFont } from '@apps/greetingCard/components/Premium/useLoadGoogleFont';
import { useDashboardContentV3Telemetry } from '../../DashboardContentV3.telemetry';
import { SkeletonThumbnail } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';

const EventDataButton: ComponentWithAs<'div', FlexProps & { label: string; cta: string }> = ({ label, cta, ...props }) => (
  <Flex
    role="group"
    flex={1}
    alignItems={'center'}
    justifyContent={'center'}
    height={7}
    paddingX={4}
    borderRadius={4}
    backgroundColor={'transparent'}
    overflow={'hidden'}
    transition={animationTransition('box-shadow', 'background-color')}
    _hover={{ boxShadow: boxShadowValuesV2.xxxl, backgroundColor: 'white' }}
    _active={{ boxShadow: boxShadowValuesV2.hardxs, backgroundColor: 'white' }}
    {...props}
  >
    <Box
      paddingTop={1}
      marginTop={-1}
      transform={'translateY(25%)'}
      textAlign={'center'}
      transition={animationTransition('transform')}
      cursor={'pointer'}
      overflow={'hidden'}
      _groupHover={{
        transform: 'translateY(-25%)'
      }}
    >
      <TextV2
        typographyVariant={'button3'}
        color={'mono10'}
        opacity={1}
        overflow={'hidden'}
        whiteSpace={'nowrap'}
        textOverflow={'ellipsis'}
        transition={animationTransition('opacity')}
        _groupHover={{ opacity: 0 }}
      >
        {label}
      </TextV2>
      <TextV2
        paddingTop={1}
        typographyVariant={'button3'}
        color={'mono10'}
        opacity={0}
        transition={animationTransition('opacity', 'color')}
        overflow={'hidden'}
        whiteSpace={'nowrap'}
        textOverflow={'ellipsis'}
        _groupHover={{ opacity: 1, color: 'linkHover' }}
        _groupActive={{ opacity: 1, color: 'linkActive' }}
      >
        {cta}
      </TextV2>
    </Box>
  </Flex>
);

export const EventInfoColumn = ({ eventHandle }: { eventHandle: string }) => {
  const telemetry = useDashboardContentV3Telemetry();

  const { loading, data } = useGetWebsitePreviewDataQuery({
    variables: { eventHandle },
    batchMode: 'fast',
    onCompleted: () => telemetry.dashboardV3DataLoaded()
  });

  const isFontLoaded = useLoadGoogleFont(`${data?.eventByName?.eventDesign?.font?.fontFamily}:400`);

  const { t2 } = useTranslation('adminDashboardV3');
  const mainEventDetailsTranslations = t2('mainEventDetails');
  const mainStatsTranslations = t2('mainStats');

  const router = useRouterHelper();

  const handleEventDetailsClick = (type: 'date' | 'location') => {
    telemetry.clickEventDetails(type);
  };

  const handleStatClick = (stat: Stat['id']) => {
    telemetry.clickWebsiteStats(stat);
  };

  if (loading || !isFontLoaded) return <SkeletonThumbnail borderRadius={3} height={pxToRem(296)} width={'100%'} />;

  return (
    <>
      <Box
        paddingTop={{
          md: 3
        }}
      >
        <DashboardWebsitePreview eventHandle={eventHandle} onPreviewClick={() => telemetry.clickWebsitePreview()} onDesignClick={() => telemetry.clickWebsiteDesign()} />
      </Box>
      <TextV2
        fontSize={1500}
        fontFamily={data?.eventByName?.eventDesign?.font.fontFamily}
        textAlign={'center'}
        wordBreak={'break-word'}
        paddingTop={{
          _: 6,
          md: 9
        }}
      >{`${data?.eventByName?.info.ownerFirstName} & ${data?.eventByName?.info.fianceeFirstName}`}</TextV2>
      <Flex
        justifyContent={'center'}
        paddingTop={3}
        flexDirection={{
          _: 'column',
          md: 'row'
        }}
        rowGap={4}
        columnGap={4}
      >
        {data?.eventByName?.info.finalizedDate?.dateString && (
          <EventDataButton
            label={format(new Date(data?.eventByName?.info.finalizedDate?.dateString), 'MMMM do, yyyy')}
            cta={mainEventDetailsTranslations.dateCta}
            as={'a'}
            href={router.buildPath(routePaths.settings.path)}
            onClick={() => handleEventDetailsClick('date')}
          />
        )}
        {data?.eventByName?.info.location && (
          <EventDataButton
            label={data?.eventByName?.info.location}
            cta={mainEventDetailsTranslations.locationCta}
            as={'a'}
            href={router.buildPath(routePaths.settings.path)}
            onClick={() => handleEventDetailsClick('location')}
          />
        )}
      </Flex>
      <DashboardStat marginTop={6}>
        <DashboardStat.Row>
          <DashboardStat.ItemDefault
            value={data?.eventByName?.guests.peopleCount || 0}
            color={'accentBlue3'}
            icon={<GuestList />}
            cta={mainStatsTranslations.potentialGuestsLabel}
            ctaHover={mainStatsTranslations.potentialGuestsCta}
            link={router.buildPath(routePaths.guests.path)}
            onClick={() => handleStatClick('potentialGuests')}
          />
          <DashboardStat.ItemDefault
            value={data?.eventByName?.guests.people.filter(person => person.legacyRsvpAnswers?.find(answer => answer?.questionId === 'rsvp')?.answer === 'true').length || 0}
            color={'accentBlue4'}
            icon={<CheckOutline />}
            cta={mainStatsTranslations.attendingLabel}
            ctaHover={mainStatsTranslations.attendingCta}
            link={router.buildPath(routePaths.rsvp.path)}
            onClick={() => handleStatClick('attending')}
          />
          <DashboardStat.ItemDefault
            value={data?.eventByName?.registry.itemCount.available || 0}
            color={'accentBlue5'}
            icon={<Gift />}
            cta={mainStatsTranslations.giftsAvailableLabel}
            ctaHover={mainStatsTranslations.giftsAvailableCta}
            link={router.buildPath(routePaths.registryManage.path)}
            onClick={() => handleStatClick('giftsAvailable')}
          />
        </DashboardStat.Row>
      </DashboardStat>
    </>
  );
};
